import React, { useState, useCallback } from "react";
import ReactFlow, {
    addEdge,
    Background,
    Controls,
    useNodesState,
    useEdgesState,
    ReactFlowProvider,
} from "react-flow-renderer";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Card,
    CardContent,
    Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CustomNode from "../components/CustomNode";
import { useDispatch, useSelector } from "react-redux";
import { setNodes, setEdges } from "../redux/nodemapSlice";

// Node Types Definition
const nodeTypes = {
    component: (props) => <CustomNode {...props} />,
    text: (props) => <CustomNode {...props} />,
    defaultNode: (props) => <CustomNode {...props} />,
};

const SystemsMap = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const nodesFromState = useSelector((state) => state.graph.nodes);
    const edgesFromState = useSelector((state) => state.graph.edges);

    const [nodes, setNodesLocal, onNodesChange] = useNodesState(nodesFromState || [
        {
            id: "1",
            type: "component",
            data: { label: "Default Node", content: "Drag me around" },
            position: { x: 250, y: 100 },
        },
    ]);

    const [edges, setEdgesLocal, onEdgesChange] = useEdgesState(edgesFromState || []);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentNode, setCurrentNode] = useState({
        id: "",
        label: "",
        description: "",
        type: "component",
    });

    const onConnect = useCallback(
        (params) => {
            const newEdge = {
                ...params,
                animated: true,
                style: { stroke: theme.palette.primary.main, strokeWidth: 3 },
            };
            setEdgesLocal((eds) => addEdge(newEdge, eds));
            dispatch(setEdges([...edges, newEdge]));
        },
        [setEdgesLocal, edges, dispatch, theme.palette.primary.main]
    );

    const addNewNode = () => {
        if (!currentNode.label.trim()) {
            alert("Node title cannot be empty");
            return;
        }

        const newNode = {
            id: `${nodes.length + 1}`,
            data: {
                label: currentNode.label,
                content: currentNode.description,
                type: currentNode.type,
            },
            position: { x: Math.random() * 400, y: Math.random() * 400 },
            type: currentNode.type,
        };

        setNodesLocal((nds) => [...nds, newNode]);
        dispatch(setNodes([...nodes, newNode]));
        setDialogOpen(false);
    };

    const addTextNode = () => {
        const newNode = {
            id: `${nodes.length + 1}`,
            data: {
                label: "",
                content: "",
                type: "text",
            },
            position: { x: Math.random() * 400, y: Math.random() * 400 },
            type: "text",
        };

        setNodesLocal((nds) => [...nds, newNode]);
        dispatch(setNodes([...nodes, newNode]));
    };

    const openAddNodeModal = (type) => {
        setCurrentNode({ id: "", label: "", description: "", type });
        setDialogOpen(true);
    };

    const handleDelete = () => {
        const selectedNodes = nodes.filter((node) => node.selected);
        const selectedEdges = edges.filter((edge) => edge.selected);

        if (selectedNodes.length > 0) {
            const remainingNodes = nodes.filter((node) => !node.selected);
            const remainingEdges = edges.filter(
                (edge) =>
                    !selectedNodes.some(
                        (node) => edge.source === node.id || edge.target === node.id
                    )
            );
            setNodesLocal(remainingNodes);
            setEdgesLocal(remainingEdges);
            dispatch(setNodes(remainingNodes));
            dispatch(setEdges(remainingEdges));
        } else if (selectedEdges.length > 0) {
            const remainingEdges = edges.filter((edge) => !edge.selected);
            setEdgesLocal(remainingEdges);
            dispatch(setEdges(remainingEdges));
        }
    };

    return (
        <ReactFlowProvider>
            <div style={{ height: "90vh", position: "relative" }}>
                {/* React Flow Area */}
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onConnect={onConnect}
                    fitView
                    nodeTypes={nodeTypes}
                    selectionOnDrag
                >
                    <Controls />
                    <Background />
                </ReactFlow>

                {/* Floating Node Action Panel */}
                <Card
                    sx={{
                        position: "absolute",
                        top: 16,
                        right: 16,
                        width: 260,
                        p: 2,
                        zIndex: 10,
                        backgroundColor: "background.paper",
                        color: "text.primary",
                        border: 1,
                        borderColor: "divider",
                    }}
                >
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Add Nodes
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            sx={{ mb: 2 }}
                            onClick={() => openAddNodeModal("component")}
                        >
                            Add Component Node
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            fullWidth
                            onClick={addTextNode}
                        >
                            Add Text Node
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            fullWidth
                            sx={{ mt: 2 }}
                            onClick={handleDelete}
                        >
                            Delete Selected
                        </Button>
                    </CardContent>
                </Card>

                {/* Node Dialog */}
                <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                    <DialogTitle>Add Component Node</DialogTitle>
                    <DialogContent>
                        <TextField
                            fullWidth
                            label="Node Title"
                            value={currentNode.label}
                            onChange={(e) =>
                                setCurrentNode({ ...currentNode, label: e.target.value })
                            }
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            label="Node Description"
                            value={currentNode.description}
                            onChange={(e) =>
                                setCurrentNode({ ...currentNode, description: e.target.value })
                            }
                            margin="normal"
                            multiline
                            rows={3}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button onClick={addNewNode} variant="contained" color="primary">
                            Add
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        </ReactFlowProvider>
    );
};

export default SystemsMap;
